<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  BookOpen,
  CalendarDays,
  BadgePoundSterling,
  PlayCircle,
  ChevronRight,
} from 'lucide-vue-next'
import { truncate } from 'lodash'
import { planPriceSummary, lowestAvailablePrice, BINDLE_LIVE_ON_SALE } from '@/composables/useTutorPlans'

interface Subject {
  id: number
  name: string
  color: string
}

interface Examboard {
  id: number
  name: string
}

interface ALevel {
  subject: string
  grade: string
  color?: string
}

interface Degree {
  degree_type?: string
  title?: string
  institution?: string
  start_year?: string
  end_year?: string
  status?: string
  classification?: string
}

interface AdditionalQualification {
  name: string
  year?: string
}

interface AcademicQualifications {
  degrees: Degree[]
  alevels: ALevel[]
  years_of_experience?: number | string
  additional_qualifications: AdditionalQualification[]
}

interface TimeSlot {
  day: string
  time: string
  available: boolean
  booked?: boolean
  locked?: boolean
}

interface LegendItem {
  label: string
  bg: string
}

interface Subscription {
  teacher_id: number
  tier: string
}

interface Props {
  tutor: {
    id: number
    slug: string
    first_name: string
    about_me?: string
    subjects: Subject[]
    examboards: Examboard[]
    teaching?: Array<{ subject_id: number; subject: string; levels: string[]; examboards: string[] }>
    academic_qualifications: AcademicQualifications
    services_offered: string[]
    price_gold_tier: number
    videos_count: number
  }
  availability: TimeSlot[][]
  legend?: LegendItem[]
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  subscriptions: () => [],
})

const emit = defineEmits<{ (e: 'open-tab', tab: string): void }>()

/**
 * Overview summarises every other tab, and holds none of their contents.
 *
 * It used to be one of six peers carrying the biography and the full subjects
 * table, so a reader landing on the profile got a long scroll of some things and
 * no sign that the rest existed. Every tab now has a card here: a headline fact
 * large enough to read at a glance, the detail underneath, and the tab itself
 * one click away. Nothing on this page is the only place its information
 * appears, which is what lets it stay short.
 */

const pluralise = (count: number, word: string): string =>
  `${count} ${word}${count === 1 ? '' : 's'}`

/** Every subject taught, and every distinct level across them. */
const subjectNames = computed(() =>
  (props.tutor.teaching ?? []).map((entry) => entry.subject)
    .concat((props.tutor.teaching ?? []).length ? [] : (props.tutor.subjects ?? []).map((s) => s.name))
)

const levelNames = computed(() => [
  ...new Set((props.tutor.teaching ?? []).flatMap((entry) => entry.levels)),
])

const openSlots = computed(() =>
  props.availability.flat().filter((slot) => slot.available && !slot.booked).length
)

/**
 * Which days, not just how many slots — a parent looking for a Saturday needs
 * to know before opening the timetable. The stored days are shouted (`MON`), so
 * they are cased down here rather than left to look like an abbreviation the
 * reader is meant to recognise.
 */
const openDays = computed(() => [
  ...new Set(
    props.availability.flat()
      .filter((slot) => slot.available && !slot.booked)
      .map((slot) => slot.day.charAt(0).toUpperCase() + slot.day.slice(1).toLowerCase())
  ),
])

const availabilityDetail = computed(() => {
  if (!openDays.value.length) {
    return `No open slots on ${props.tutor.first_name}'s timetable this week.`
  }

  return openDays.value.length >= 7
    ? 'Free every day this week.'
    : `Free on ${openDays.value.join(', ')} this week.`
})

const priceInput = computed(() => ({
  goldPrice: Number(props.tutor.price_gold_tier),
  servicesOffered: props.tutor.services_offered ?? [],
}))

/**
 * A card for each tab a reader can go to, in the tab strip's order.
 *
 * Qualifications and Reviews are not among them. The qualifications live in the
 * identity card above, which stays on screen whichever tab is open, so summarising
 * them here was a third copy of the same facts; reviews are not built, and a card
 * whose only news is "coming soon" costs a reader a glance and tells them nothing.
 *
 * `headline` is the fact; `detail` is the sentence that makes it mean something.
 * A card with no fact to report says so plainly rather than being dropped,
 * because a missing card reads as a missing tab.
 */
const cards = computed(() => [
  {
    tab: 'subjects',
    icon: BookOpen,
    title: 'Subjects tutored',
    // The subjects themselves, because "do you teach my child's subject" is the
    // question the profile is opened to answer. The levels are a count: naming
    // all eight repeats the sidebar and buries the subjects underneath them.
    headline: subjectNames.value.length ? subjectNames.value.join(', ') : 'Not recorded yet',
    detail: subjectNames.value.length
      ? levelNames.value.length
        ? `Taught at ${pluralise(levelNames.value.length, 'level')}, ${levelNames.value.slice(0, 2).join(' and ')} among them, each with its own exam boards.`
        : 'Levels and exam boards are listed on the tab.'
      : `${props.tutor.first_name} has not listed their subjects yet.`,
  },
  {
    tab: 'availability',
    icon: CalendarDays,
    title: 'Weekly availability',
    headline: openSlots.value ? pluralise(openSlots.value, 'open slot') : 'Fully booked',
    detail: availabilityDetail.value,
  },
  {
    tab: 'plans',
    icon: BadgePoundSterling,
    title: 'Plans',
    headline: lowestAvailablePrice(priceInput.value) !== null
      ? `£${lowestAvailablePrice(priceInput.value)!.toFixed(2)} a week`
      : 'Not on sale yet',
    detail: [
      planPriceSummary(priceInput.value),
      BINDLE_LIVE_ON_SALE ? null : 'Bindle Live group classes are coming soon.',
    ].filter(Boolean).join(' · '),
  },
  {
    tab: 'content',
    icon: PlayCircle,
    title: 'Content library',
    headline: props.tutor.videos_count ? pluralise(props.tutor.videos_count, 'video') : 'Nothing published yet',
    detail: props.tutor.videos_count
      ? 'Included with every plan, watch as many times as you like.'
      : `${props.tutor.first_name}'s study material appears here as it is published.`,
  },
])

const maxLength = 420
const aboutFullDisplayed = ref(false)
</script>

<template>
  <div class="space-y-8">
    <div>
      <h3 class="mb-3 text-xl font-semibold text-ink">About {{ tutor.first_name }}</h3>
      <p class="leading-relaxed whitespace-pre-line text-muted">
        {{
          (aboutFullDisplayed ? tutor.about_me : truncate(tutor.about_me, { length: maxLength }))
            || 'Experienced tutor with a passion for helping students achieve their academic goals.'
        }}
        <button
          v-if="!aboutFullDisplayed && (tutor.about_me?.length ?? 0) > maxLength"
          type="button"
          class="font-semibold text-brand hover:underline"
          @click="aboutFullDisplayed = true"
        >
          Read more
        </button>
      </p>
    </div>

    <!-- A card per tab: the headline fact, the sentence around it, and the way in -->
    <div>
      <h3 class="mb-3 text-xl font-semibold text-ink">At a glance</h3>

      <div class="grid gap-3 sm:grid-cols-2">
        <button
          v-for="card in cards"
          :key="card.tab"
          type="button"
          class="group flex flex-col rounded-xl border border-line bg-surface p-4 text-left transition-all duration-200 ease-brand hover:-translate-y-0.5 hover:border-line-brand hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          @click="emit('open-tab', card.tab)"
        >
          <span class="flex items-center gap-2.5">
            <!--
              Bundled icons rather than the Material Symbols webfont: a ligature
              font renders its own name as words for as long as it takes to load.
            -->
            <span class="grid size-8 shrink-0 place-items-center rounded-full bg-brand-soft text-brand transition-transform duration-200 ease-brand group-hover:scale-110">
              <component :is="card.icon" class="size-4" aria-hidden="true" />
            </span>
            <span class="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-[0.08em] text-subtle">
              {{ card.title }}
            </span>
            <ChevronRight class="size-4 shrink-0 text-subtle transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-brand" />
          </span>

          <span class="mt-3 block font-display text-lg leading-tight text-ink">{{ card.headline }}</span>
          <span class="mt-1 block text-sm leading-snug text-muted">{{ card.detail }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
